import { useState, useEffect } from 'react';
import { Redirect, useLocation } from 'react-router';

import { ATSConnectHiringRequestTestIds } from 'data-testids/ATS';
import { ConnectCandidateRowSkeleton } from './CandidateList/CandidateRow/components/ConnectCandidateRowSkeleton';
import { ConnectCandidatesList } from './CandidateList/ConnectCandidatesList';
import { Container, Title } from './styles/hiringRequestStyles';
import { HiringRequestsFormModal } from './CandidateImport/HiringRequestsFormModal';
import { useQueryGetHiringRequestListData } from './api/referenceQueries';
import auth from 'utils/auth';
import { Alert } from 'sharedComponents/Alert';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';

const unauthorizedImportErrorKey = 'unauthorizedImportError';
const unauthorizedImportValue = 'attempted';

export const HiringRequestsLandingPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const candidateId = Number(queryParams.get('candidate_id'));
  const [isOpen, setIsOpen] = useState(false);
  const districtId = auth.getUser()?.profile.district.id;
  const [showImportCandidateError, setShowImportCandidateError] = useState(false);

  const { data, error, isError, isLoading } = useQueryGetHiringRequestListData(districtId);

  const handleClose = () => setIsOpen(false);

  const handleImportCandidateErrorClose = () => {
    setShowImportCandidateError(false);
    sessionStorage.removeItem(unauthorizedImportErrorKey);
  };

  useEffect(() => {
    const hasUnauthorizedImportError =
      sessionStorage.getItem(unauthorizedImportErrorKey) === unauthorizedImportValue;
    const isValidCandidateId = Number.isInteger(candidateId) && candidateId > 0;

    setIsOpen(isValidCandidateId && !hasUnauthorizedImportError);
    setShowImportCandidateError(hasUnauthorizedImportError);
  }, [candidateId]);

  if (!auth.hasConnectCandidateProcessing()) {
    const redirectPrefix = auth.isSchoolAdmin() ? '/school' : '/district';
    return <Redirect to={`${redirectPrefix}/home/`} />;
  }

  if (!auth.isSchoolAdmin() && candidateId) {
    sessionStorage.setItem(unauthorizedImportErrorKey, unauthorizedImportValue);
    return <Redirect to="/hiring-requests" />;
  }

  if (isError) {
    const message = getAxiosErrorMessageByStatusCode(error);
    return <Alert type="error">{message}</Alert>;
  }

  return (
    <>
      <ErrorSnackbar
        open={showImportCandidateError}
        onClose={handleImportCandidateErrorClose}
        message="Submitting hiring requests is only available to school admin at this time."
        duration={5000}
        dataTestId={ATSConnectHiringRequestTestIds.UNAUTHORIZED_IMPORT_CANDIDATE_ERROR}
      />
      <Container>
        <Title data-testid={ATSConnectHiringRequestTestIds.HIRING_REQUEST_TITLE} variant="h2">
          Connect candidates
        </Title>
        {isLoading && !data ? (
          <ConnectCandidateRowSkeleton />
        ) : (
          <ConnectCandidatesList results={data?.results} />
        )}
      </Container>
      <HiringRequestsFormModal
        isOpen={isOpen}
        candidateId={candidateId}
        handleClose={handleClose}
      />
    </>
  );
};
